import React from 'react';
import axios from 'axios';
import { Button, Col, Container, Row } from 'react-materialize';
import LoadingView from '../loading/loading.view';
import Modal from './modal';
import Swal from 'sweetalert2';
import AsyncSelect from 'react-select/async';
import { ErpConnect as qad } from '../../../services';
import './inventory-report.css';
import {
  cachedFetchJson,
  enqueueCount,
  flushCounts,
  outboxCount
} from '../../../offline/inventoryOfflineStore';

const QAD_CACHE_PREFIX = 'qad_cache_v1:';
const QAD_CACHE_TTL_MS = 1000 * 60 * 60 * 24 * 30;

function nowMs() {
  return Date.now();
}

function ymdLocal(d = new Date()) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}


function stableStringify(obj) {
  if (!obj || typeof obj !== 'object') return JSON.stringify(obj);
  const keys = Object.keys(obj).sort();
  const out = {};
  keys.forEach((k) => {
    out[k] = obj[k];
  });
  return JSON.stringify(out);
}

function safeJsonParse(s, fallback) {
  try {
    return JSON.parse(s);
  } catch {
    return fallback;
  }
}

function qadCacheKey(kind, params) {
  return `${QAD_CACHE_PREFIX}${kind}::${stableStringify(params || {})}`;
}

function qadCacheRead(key) {
  const raw = localStorage.getItem(key);
  if (!raw) return null;
  const payload = safeJsonParse(raw, null);
  if (!payload || typeof payload !== 'object') return null;
  const ts = Number(payload.ts || 0);
  if (!ts) return null;
  if (nowMs() - ts > QAD_CACHE_TTL_MS) {
    localStorage.removeItem(key);
    return null;
  }
  return payload.data ?? null;
}

function qadCacheWrite(key, data) {
  const payload = { ts: nowMs(), data };
  try {
    localStorage.setItem(key, JSON.stringify(payload));
  } catch {
  }
}

async function qadReadCached(kind, params, fetcher) {
  const key = qadCacheKey(kind, params);

  if (navigator.onLine) {
    try {
      const data = await fetcher();
      qadCacheWrite(key, data);
      return data;
    } catch (e) {
      const cached = qadCacheRead(key);
      if (cached != null) return cached;
      throw e;
    }
  }

  const cached = qadCacheRead(key);
  if (cached != null) return cached;
  throw new Error('OFFLINE_NO_CACHE');
}

const formatCountedAt = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  return new Intl.DateTimeFormat('es-MX', {
    timeZone: 'America/Monterrey',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  }).format(d);
};

const groupReportRowsByBrand = (rows = []) => {
  const sorted = [...(Array.isArray(rows) ? rows : [])].sort((a, b) => {
    const ta = a?.counted_at ? new Date(a.counted_at).getTime() : 0;
    const tb = b?.counted_at ? new Date(b.counted_at).getTime() : 0;
    return ta - tb;
  });

  const map = new Map();
  for (const r of sorted) {
    const brand = (r?.brand_name || 'Sin marca').toString().trim() || 'Sin marca';
    if (!map.has(brand)) map.set(brand, []);
    map.get(brand).push(r);
  }

  const groups = Array.from(map.entries()).map(([brand, items]) => {
    const totalQty = items.reduce((acc, x) => acc + (Number(x?.qty) || 0), 0);
    const firstAt = items[0]?.counted_at || null;
    const lastAt = items[items.length - 1]?.counted_at || null;
    return { brand, items, totalQty, firstAt, lastAt };
  });

  groups.sort((a, b) => {
    const ta = a?.lastAt ? new Date(a.lastAt).getTime() : 0;
    const tb = b?.lastAt ? new Date(b.lastAt).getTime() : 0;
    return tb - ta;
  });

  return groups;
};

class ModalWarehouse extends React.Component {
  state = {
    loading: false,
    brand: null,
    location: null,
    patterns: [],
    loadingPatterns: false,
    pressedButtonId: null,
    products: { colorKeys: [], colorItems: [] },
    header: {},
  
    reportDate: ymdLocal(),         
    reportSessionId: null,           
    reportSessionDate: null,         
  
    adminAssignLocation: null,
    adminAgent: null,
    assigningLocation: false,
    replaceAllLocations: true,       
  
    reportAgent: null,
    reportRows: [],
    reportLoading: false,
    reportBrands: [],
    reportBrandSelected: 'ALL',      
    reportBrandFilter: 'ALL'
  };
  
  user = JSON.parse(localStorage.getItem('user')) || {};
  _agentsCache = null;

  norm = (v) => (v || '').toString().trim();

  getSite = () => (this.props.site || '3000').toString().trim() || '3000';

  getInventoryHeaders = () => ({
    'X-Inventory-User': JSON.stringify(this.user || {})
  });

  getInventoryAgentId = () => {
    const u = this.user || {};
    const raw = u.salesPersonId || u.id || u.userId || u.agent || u.username || u.usr || '';
    return (raw || '').toString().trim().toUpperCase();
  };

  isInventoryAdmin = () => {
    const u = this.user || {};
    const agent = (u.agent || u.username || '').toLowerCase();
    const spId = (u.salesPersonId || '').toUpperCase();
    return agent === 'jdomingu' || spId === 'VJOD' || agent === 'esanchez' || spId === 'VELS';
  };

  loadReportBrands = async () => {
    try {
      const params = { site: this.getSite() };
      const res = await axios.get('/api/inventory/brands', {
        params,
        headers: this.getInventoryHeaders()
      });
  
      const brandsRaw =
        res.data?.brands ??
        res.data?.data ??
        res.data?.rows ??
        res.data ??
        [];
  
      const brands = (Array.isArray(brandsRaw) ? brandsRaw : [])
        .map((b) => (b || '').toString().trim())
        .filter(Boolean);
  
      const brandsWithAll = ['ALL', ...brands];
  
      this.setState({
        reportBrands: brandsWithAll,
        reportBrandSelected: 'ALL'
      });
    } catch (e) {
      console.error(e);
      this.setState({ reportBrands: ['ALL'], reportBrandSelected: 'ALL' });
    }
  };
    
  async componentDidMount() {
    if (this.isInventoryAdmin()) {
      await this.loadReportBrands();
        }
    window.addEventListener('online', this.flushOutboxCounts);
    this.flushOutboxCounts();
  }

  componentWillUnmount() {
    window.removeEventListener('online', this.flushOutboxCounts);
  }

  _isFlushing = false;

  flushOutboxCounts = async () => {
    if (this._isFlushing) return;
    if (!navigator.onLine) return;

    this._isFlushing = true;
    try {
      const result = await flushCounts(
        async (payload) => {
          await axios.post('/api/inventory/counts', payload, {
            headers: this.getInventoryHeaders()
          });
        },
        { max: 50 }
      );

      if (result?.sent > 0) {
        Swal.fire(
          'Sincronizado',
          `Se enviaron ${result.sent} conteos pendientes. Pendientes: ${result.left}.`,
          'success'
        );
      }
    } catch (e) {
      console.error(e);
    } finally {
      this._isFlushing = false;
    }
  };

  loadAgents = async (inputValue) => {
    try {
      if (!this._agentsCache) {
        const all = await qad.catalog.salesPerson.all();
        this._agentsCache = Array.isArray(all) ? all : [];
      }

      const term = (inputValue || '').toLowerCase().trim();
      return (this._agentsCache || [])
        .filter((sp) => {
          const id = (sp.id || '').toLowerCase();
          const name = (sp.name || '').toLowerCase();
          return !term || id.includes(term) || name.includes(term);
        })
        .slice(0, 40)
        .map((sp) => ({
          label: `${sp.id} - ${sp.name}`,
          value: sp.id,
          name: sp.name || ''
        }));
    } catch (e) {
      console.error(e);
      return [];
    }
  };

  RACK_LIMITS = { P1: 23, P2: 24, P3: 21 };

  buildRackOptions = (termRaw = '') => {
    const term = (termRaw || '').toString().trim().toUpperCase();
    const opts = [];
    const pisos = Object.keys(this.RACK_LIMITS);

    pisos.forEach((piso) => {
      const max = Number(this.RACK_LIMITS[piso] || 0);
      for (let i = 1; i <= max; i += 1) {
        const rack = `${piso}-R${i}`;
        opts.push({ label: rack, value: rack });
      }
    });

    opts.push({ label: 'P2-E', value: 'P2-E' });
    opts.push({ label: 'LOCAL', value: 'LC' });

    if (!term) return opts;
    return opts.filter((o) => o.value.includes(term) || o.label.includes(term));
  };

  loadAdminAssignRacks = async (inputValue) => {
    return this.buildRackOptions(inputValue).slice(0, 80);
  };

  loadMyLocations = async (inputValue) => {
    const site = this.getSite();
    const q = (inputValue || '').toString().trim().toUpperCase();
    const agent_id = this.getInventoryAgentId();

    try {
      const params = { site, agent_id };
      if (q) params.q = q;

      const data = await cachedFetchJson('/api/inventory/locations', {
        params,
        headers: this.getInventoryHeaders(),
        ttlMs: 1000 * 60 * 60 * 24,
        cacheKeyExtra: `locations_${site}_${agent_id}`
      });

      const raw = data?.locations ?? data?.data ?? data?.rows ?? data ?? [];
      const list = Array.isArray(raw) ? raw : [];

      return list
        .map((x) => {
          if (typeof x === 'string') return x;
          return x.location || x.value || x.label || '';
        })
        .map((s) => (s || '').toString().trim().toUpperCase())
        .filter(Boolean)
        .map((loc) => ({ label: loc, value: loc }));
    } catch (e) {
      console.error(e);
      return [];
    }
  };

  handleCloseModal = () => {
    this.setState({
      loading: false,
      brand: null,
      location: null,

      patterns: [],
      loadingPatterns: false,
      pressedButtonId: null,

      products: { colorKeys: [], colorItems: [] },
      header: {},
      reportDate: ymdLocal(),
      reportSessionId: null,
      reportSessionDate: null,
      
      adminAssignLocation: null,
      adminAgent: null,
      assigningLocation: false,

      reportAgent: null,
      reportRows: [],
      reportLoading: false,

      reportBrandFilter: 'ALL'
    });

    this.props.onClose && this.props.onClose();
  };

  handleSaveInventory = async () => {
    const { brand, location, products } = this.state;

    const locationValue = (location && location.value) || '';
    const locationTrim = (locationValue || '').toString().trim();

    if (!locationTrim) {
      Swal.fire('Falta ubicación', 'Selecciona una ubicación asignada antes de guardar.', 'warning');
      return;
    }
    if (!brand) {
      Swal.fire('Falta marca', 'Selecciona una marca antes de guardar.', 'warning');
      return;
    }

    const items = [];
    (products.colorItems || []).forEach((color) => {
      (color.genderItems || []).forEach((gender) => {
        (gender.sizeItems || []).forEach((sizeItem) => {
          (sizeItem.products || []).forEach((product) => {
            const qty = Number(product.qty || 0);
            if (qty > 0 && product.id && product.article) {
              items.push({
                product_id: product.id,
                article: product.article,
                brand_id: brand.value,
                brand_name: brand.label,
                color_label: color.colorName,
                size_label: product.sizeLabel || product.sizeName,
                qty
              });
            }
          });
        });
      });
    });

    if (!items.length) {
      Swal.fire('Sin datos', 'No hay cantidades capturadas (todas están en 0).', 'warning');
      return;
    }

    const totalQty = items.reduce((sum, it) => sum + it.qty, 0);
    const resumenHtml = items
      .map((it) => `${it.article} - ${it.color_label || ''} ${it.size_label || ''}: <b>${it.qty}</b>`)
      .join('<br/>');

    const user = this.user || {};
    const counted_by_id = user.salesPersonId || user.id || user.userId || user.username || user.usr || null;
    const counted_by_name = user.name || user.fullName || user.salesPersonName || user.username || null;

    const payload = {
      site: this.getSite(),
      session_date: ymdLocal(),
      location: locationTrim,
      counted_by_id,
      counted_by_name,
      items
    };
    
    if (!navigator.onLine) {
      enqueueCount(payload);
      Swal.fire(
        'Guardado sin señal',
        `Se guardó en cola local. Pendientes: ${outboxCount()}. Al volver internet se enviará automáticamente.`,
        'info'
      );
      return;
    }

    try {
      const res = await axios.post('/api/inventory/counts', payload, {
        headers: this.getInventoryHeaders()
      });

      if (res.data && res.data.ok) {
        Swal.fire({
          icon: 'success',
          title: 'Conteo guardado',
          html: `
            <p><b>Ubicación:</b> ${locationTrim}</p>
            <p><b>Marca:</b> ${brand.label}</p>
            <p><b>Total de piezas:</b> ${totalQty}</p>
            <hr/>
            <div style="max-height:260px;overflow:auto;text-align:left;font-size:13px">
              ${resumenHtml}
            </div>
          `
        });

        const resetProducts = { ...products };
        resetProducts.colorItems = (resetProducts.colorItems || []).map((color) => ({
          ...color,
          genderItems: (color.genderItems || []).map((gender) => ({
            ...gender,
            sizeItems: (gender.sizeItems || []).map((sizeItem) => ({
              ...sizeItem,
              products: (sizeItem.products || []).map((p) => ({ ...p, qty: 0 }))
            }))
          }))
        }));

        this.setState({ products: resetProducts });
      } else {
        Swal.fire('Error', res.data?.message || 'No se pudo guardar el conteo de inventario.', 'error');
      }
    } catch (e) {
      console.error(e);
      enqueueCount(payload);
      Swal.fire('Guardado en cola', `No se pudo enviar por red (se encoló local). Pendientes: ${outboxCount()}.`, 'warning');
    }
  };

  handleAssignLocationToAgent = async () => {
    const { adminAssignLocation, adminAgent, replaceAllLocations } = this.state;
  
    const rack_code = (adminAssignLocation?.value || '').toString().trim().toUpperCase();
    const agentId = (adminAgent?.value || '').toString().trim();
    const agentName = (adminAgent?.name || '').toString().trim();
  
    if (!rack_code) {
      Swal.fire('Falta rack', 'Selecciona el rack base (ej. P1-R19) para asignar.', 'warning');
      return;
    }
    if (!agentId) {
      Swal.fire('Falta agente', 'Selecciona el agente al que asignarás el rack.', 'warning');
      return;
    }
  
    this.setState({ assigningLocation: true });
  
    try {
      const payload = {
        site: this.getSite(),
        location: rack_code,
        agent_id: agentId,
        agent_name: agentName || null,
        replace_all: !!replaceAllLocations 
      };
  
      const res = await axios.post('/api/inventory/assign-location', payload, {
        headers: this.getInventoryHeaders()
      });
  
      if (res.data?.ok) {
        const msg = replaceAllLocations
          ? `Rack ${rack_code} asignado (reemplazó todas las ubicaciones previas) a ${adminAgent.label}.`
          : `Rack ${rack_code} asignado a ${adminAgent.label}.`;
  
        Swal.fire('Listo', msg, 'success');
        this.setState({ adminAssignLocation: null });
        this.handleLoadReport();
      } else {
        Swal.fire('Error', res.data?.message || 'No se pudo asignar el rack.', 'error');
      }
    } catch (e) {
      console.error(e);
      Swal.fire('Error', e?.message || 'Ocurrió un error al asignar el rack.', 'error');
    } finally {
      this.setState({ assigningLocation: false });
    }
  };
      
  handleClearAgentLocations = async () => {
    try {
      const agent = this.state.adminAgent;
  
      if (!agent?.value) {
        return Swal.fire('Falta agente', 'Selecciona el agente al que deseas limpiar ubicaciones.', 'warning');
      }
  
      const confirm = await Swal.fire({
        icon: 'warning',
        title: '¿Borrar TODAS las ubicaciones?',
        html: `<div style="text-align:left">
                Agente: <b>${agent.label || agent.value}</b><br/>
                Esto elimina todas sus asignaciones en este site.
              </div>`,
        showCancelButton: true,
        confirmButtonText: 'Sí, borrar',
        cancelButtonText: 'Cancelar'
      });
  
      if (!confirm.isConfirmed) return;
  
      this.setState({ assigningLocation: true });
  
      const res = await axios.delete('/api/inventory/assign-location', {
        params: {
          site: this.getSite(),
          agent_id: agent.value,
          mode: 'all'
        },
        headers: this.getInventoryHeaders()
      });
  
      const deleted = Number(res.data?.deleted || 0);
  
      Swal.fire('Listo', `Se borraron ${deleted} asignaciones.`, 'success');
  
      this.setState({ adminAssignLocation: null });
  
      this.handleLoadReport();
    } catch (e) {
      console.error(e);
      Swal.fire('Error', e?.response?.data?.message || e?.message || 'No se pudo limpiar.', 'error');
    } finally {
      this.setState({ assigningLocation: false });
    }
  };
  handleEditCountLine = async (row) => {
    try {
      if (!row?.id) {
        return Swal.fire('Sin ID', 'Esta fila no trae id. Revisa que el backend esté mandando c.id.', 'warning');
      }
  
      const currentQty = Number(row.qty || 0);
      const currentNotes = (row.notes || '').toString();
  
      const { value: form } = await Swal.fire({
        title: 'Editar conteo',
        html:
          `<div style="text-align:left;font-size:13px;opacity:.85;margin-bottom:10px">
             <b>${row.product_id || '-'}</b> · ${row.color_label || '-'} · ${row.size_label || '-'}<br/>
             Ubicación: <b>${row.location || '-'}</b>
           </div>` +
          `<input id="swal-qty" class="swal2-input" placeholder="Piezas" type="number" min="0" value="${currentQty}">` +
          `<input id="swal-notes" class="swal2-input" placeholder="Notas (opcional)" value="${currentNotes.replace(/"/g, '&quot;')}">`,
        focusConfirm: false,
        showCancelButton: true,
        confirmButtonText: 'Guardar',
        cancelButtonText: 'Cancelar',
        preConfirm: () => {
          const qty = Number(document.getElementById('swal-qty').value);
          const notes = document.getElementById('swal-notes').value;
  
          if (!Number.isFinite(qty) || qty < 0) {
            Swal.showValidationMessage('Qty inválido (>= 0)');
            return null;
          }
          return { qty, notes };
        }
      });
  
      if (!form) return;
  
      await axios.put(`/api/inventory/counts/${row.id}`, form, {
        headers: this.getInventoryHeaders()
      });
  
      Swal.fire('Actualizado', 'El conteo se actualizó correctamente.', 'success');
  
      this.handleLoadReport();
    } catch (e) {
      console.error(e);
      Swal.fire('Error', e?.response?.data?.message || e?.message || 'No se pudo editar.', 'error');
    }
  };
    
  handleLoadReport = async () => {
    const { reportAgent, reportBrandSelected } = this.state;
  
    this.setState({ reportLoading: true, reportRows: [], reportSessionId: null, reportSessionDate: null });
  
    try {
      const params = { site: this.getSite() };
  
      if (this.state.reportDate) params.session_date = this.state.reportDate;
  
      // ✅ si es ALL, NO filtramos por marca
      const brandSel = (reportBrandSelected || 'ALL').toString();
      if (brandSel && brandSel !== 'ALL') {
        params.brand_name = brandSel;
      }
  
      if (reportAgent?.value) params.agent_id = reportAgent.value;
  
      const res = await axios.get('/api/inventory/counts/report', {
        params,
        headers: this.getInventoryHeaders()
      });
  
      if (res.data && res.data.ok) {
        const rows = Array.isArray(res.data.data) ? res.data.data : [];
  
        this.setState({
          reportRows: rows,
          reportLoading: false,
          reportBrandFilter: 'ALL', // ✅ al cargar, muestra todo (luego filtras por botones)
          reportSessionId: res.data.session_id || null,
          reportSessionDate: res.data.session_date || null
        });
  
        if (!rows.length) {
          Swal.fire('Sin datos', 'No se encontraron registros con el filtro actual.', 'info');
        }
      } else {
        this.setState({ reportLoading: false });
        Swal.fire('Sin datos', res.data?.message || 'No se encontraron registros.', 'info');
      }
    } catch (e) {
      console.error(e);
      this.setState({ reportLoading: false });
      Swal.fire('Error', e?.message || 'Ocurrió un error al consultar el reporte.', 'error');
    }
  };
  
  setBrand = async (brand) => {
    this.setState({
      brand,
      pressedButtonId: null,
      patterns: [],
      loadingPatterns: false,
      products: { colorKeys: [], colorItems: [] },
      header: {}
    });

    if (!brand) return;

    this.setState({ loadingPatterns: true });
    try {
      const resultPatterns = await qadReadCached(
        'patternsByBrand',
        { brandId: brand.value },
        () => qad.catalog.pattern.relatedBy({ brandId: brand.value })
      );
      const patterns = Array.isArray(resultPatterns) ? resultPatterns : [];
      this.setState({ patterns, loadingPatterns: false });
    } catch (error) {
      console.error(error);
      this.setState({ patterns: [], loadingPatterns: false });

      if (error?.message === 'OFFLINE_NO_CACHE') {
        Swal.fire(
          'Sin señal',
          'No hay modelos en caché para esta marca. Conéctate una vez y abre esta marca para cachearla.',
          'info'
        );
      }
    }
  };

  getPatternText = (pattern) => {
    const name = pattern?.name || pattern?.Name || pattern?.modelName || pattern?.description || '';
    return this.norm(name);
  };

  getProductsFallback = async (pattern) => {
    if (!pattern || !this.state.brand) return;
    this.setState({ loading: true });

    try {
      const resultProducts = await qadReadCached(
        'productsByBrandModel',
        { brandId: this.state.brand.value, modelId: pattern.id },
        () =>
          qad.catalog.product.relatedBy({
            brandId: this.state.brand.value,
            modelId: pattern.id
          })
      );

      if (!resultProducts) {
        this.setState({
          header: {},
          products: { colorKeys: [], colorItems: [] },
          loading: false
        });
        return;
      }

      let header = {};
      let productsByColor = { colorKeys: [], colorItems: [] };

      const normSizeKey = (v) => (v || '').toString().trim().toUpperCase();
      const norm = (v) => (v || '').toString().trim().toUpperCase();

      const isMadeiraPolystitch = (p) => {
        if (p?.brandId && p?.modelId) {
          return norm(p.brandId) === 'MA09' && norm(p.modelId) === 'MD085';
        }
        return (
          norm(p?.brandName) === 'MADEIRA' &&
          (norm(p?.modelName).includes('POLYSTITCH') || norm(p?.dataSheetName).includes('POLYSTITCH'))
        );
      };

      const getDisplayColor = (p) => {
        if (isMadeiraPolystitch(p)) return p.colorName || p.colorLabel || '';
        return p.colorLabel || p.colorName || '';
      };

      (Array.isArray(resultProducts) ? resultProducts : []).forEach((product) => {
        product.qty = product.qty || 0;

        if (productsByColor.colorKeys.indexOf(product.colorId) === -1) {
          let backLinear = 'linear-gradient(90deg';
          const colors = (product.colorPantoneHexa || '').split(',');
          const count = colors.filter(Boolean).length || 1;
          const backPercent = 100 / count;

          colors.forEach((c) => {
            if (c) backLinear += ', ' + c + ' ' + backPercent + '%';
          });

          if (count === 1) backLinear += ', #fff';
          backLinear += ')';

          productsByColor.colorKeys.push(product.colorId);
          productsByColor.colorItems.push({
            colorName: getDisplayColor(product),
            colorId: product.colorId,
            colorRGB: backLinear,
            genderKeys: [product.genderId],
            genderItems: [
              {
                genderName: product.genderName,
                styleName: product.styleName,
                genderId: product.genderId,
                sizeKeys: [product.sizeId],
                sizeItems: [
                  {
                    sizeName: product.sizeName,
                    sizeId: product.sizeId,
                    sizeOrder: parseInt(product.sizeOrder, 10),
                    products: [product]
                  }
                ]
              }
            ]
          });
        } else {
          const colorPosition = productsByColor.colorKeys.indexOf(product.colorId);
          let genderPosition = 0;

          if (productsByColor.colorItems[colorPosition].genderKeys.indexOf(product.genderId) === -1) {
            productsByColor.colorItems[colorPosition].genderKeys.push(product.genderId);
            productsByColor.colorItems[colorPosition].genderItems.push({
              genderName: product.genderName,
              styleName: product.styleName,
              genderId: product.genderId,
              sizeKeys: [product.sizeId],
              sizeItems: [
                {
                  sizeName: product.sizeName,
                  sizeId: product.sizeId,
                  sizeOrder: parseInt(product.sizeOrder, 10),
                  products: [product]
                }
              ]
            });
            genderPosition = productsByColor.colorItems[colorPosition].genderKeys.length - 1;
          } else {
            genderPosition = productsByColor.colorItems[colorPosition].genderKeys.indexOf(product.genderId);
            const genderNode = productsByColor.colorItems[colorPosition].genderItems[genderPosition];

            if (genderNode.sizeKeys.indexOf(product.sizeId) === -1) {
              genderNode.sizeKeys.push(product.sizeId);
              genderNode.sizeItems.push({
                sizeName: product.sizeName,
                sizeId: product.sizeId,
                sizeOrder: parseInt(product.sizeOrder, 10),
                products: [product]
              });
            } else {
              const sizePosition = genderNode.sizeKeys.indexOf(product.sizeId);
              genderNode.sizeItems[sizePosition].products.push(product);
            }
          }
        }

        const sizeKey = normSizeKey(product.sizeName);
        if (!header[product.genderId]) {
          header[product.genderId] = {
            name: product.genderName,
            styleName: product.styleName,
            id: product.genderId,
            sizesName: [sizeKey],
            sizes: [
              {
                name: product.sizeName,
                id: product.sizeId,
                order: parseInt(product.sizeOrder, 10)
              }
            ]
          };
        } else {
          const idx = header[product.genderId].sizesName.indexOf(sizeKey);
          if (idx === -1) {
            header[product.genderId].sizesName.push(sizeKey);
            header[product.genderId].sizes.push({
              name: product.sizeName,
              id: product.sizeId,
              order: parseInt(product.sizeOrder, 10)
            });
          } else {
            const current = header[product.genderId].sizes[idx];
            const nextOrder = parseInt(product.sizeOrder, 10);
            if ((current?.order ?? 9999) > nextOrder) {
              header[product.genderId].sizes[idx] = {
                name: product.sizeName,
                id: product.sizeId,
                order: nextOrder
              };
            }
          }
        }
      });

      this.setState({ header, products: productsByColor, loading: false });
    } catch (e) {
      console.error(e);
      this.setState({ loading: false });

      if (e?.message === 'OFFLINE_NO_CACHE') {
        Swal.fire(
          'Sin señal',
          'No hay productos en caché para este modelo. Conéctate una vez y abre este modelo para cachearlo.',
          'info'
        );
      }
    }
  };

  modifyProductByPath = (edit, value) => {
    const products = { ...this.state.products };
    const qty = Number(value || 0);

    const product =
      products.colorItems?.[edit.cIndex]?.genderItems?.[edit.gIndex]?.sizeItems?.[edit.sIndex]?.products?.[edit.pIndex];

    if (!product) return;

    product.qty = Number.isFinite(qty) ? qty : 0;
    this.setState({ products });
  };

  getProductEditAt = (cIndex, genderId, sizeId, sizeNameFromHeader = '') => {
    const products = this.state.products;
    const color = products?.colorItems?.[cIndex];
    if (!color) return null;

    const gIndex = (color.genderKeys || []).indexOf(genderId);
    if (gIndex < 0) return null;

    const gender = color.genderItems?.[gIndex];
    if (!gender) return null;

    let sIndex = (gender.sizeKeys || []).indexOf(sizeId);

    if (sIndex < 0 && sizeNameFromHeader) {
      const wanted = (sizeNameFromHeader || '').toString().trim().toLowerCase();
      sIndex = (gender.sizeItems || []).findIndex((s) => {
        const nm = (s?.sizeName || '').toString().trim().toLowerCase();
        return nm === wanted;
      });
    }

    if (sIndex < 0) return null;

    const sizeItem = gender.sizeItems?.[sIndex];
    const pIndex = 0;
    const product = sizeItem?.products?.[pIndex];
    if (!product) return null;

    return { product, edit: { cIndex, gIndex, sIndex, pIndex } };
  };

  getColorBackground = (color) => {
    const colorNamesImages = [
      'jaspe-obscuro',
      'amarillo-neon',
      'verde-neon',
      'marino-jaspe',
      'royal-jaspe',
      'purpura-jaspe',
      'naranja-jaspe',
      'grafito-jaspe',
      'rojo-jaspe',
      'negro-jaspe',
      'rey-jaspe',
      'vino-jaspe',
      'oxford-jaspe',
      'cherry-jaspe',
      'royal-con-jaspe',
      'rosa-neon',
      'naranja-neon',
      'gris-obscuro',
      'neon-pink',
      'neon-yellow',
      'neon-green',
      'camuflaje',
      'camuflaje-rosa',
      'rose-gold',
      'flores',
      'crema'
    ];

    let normalizedName = (color.colorName || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, '-')
      .replace(/camiflaje/g, 'camuflaje');

    const specialColors = Object.fromEntries(
      colorNamesImages.map((name) => [name, `https://miplayera.com.mx/images/pv/COLORES/${name}.png`])
    );

    const imageUrl = specialColors[normalizedName];
    if (imageUrl) {
      return { background: `url(${imageUrl}) center center / 100% 100% no-repeat` };
    }

    return { background: color.colorRGB || '#cccccc' };
  };

  handleExportReportExcel = () => {
    const { reportRows = [], reportBrandFilter } = this.state;

    const reportBrandCurrent = (reportBrandFilter || 'ALL').toUpperCase();
    const reportGroups = groupReportRowsByBrand(reportRows);

    const visibleGroups =
      reportBrandCurrent === 'ALL'
        ? reportGroups
        : reportGroups.filter((g) => (g.brand || '').toUpperCase() === reportBrandCurrent);

    const rowsToExport = [];
    visibleGroups.forEach((g) => (g.items || []).forEach((it) => rowsToExport.push(it)));

    if (!rowsToExport.length) {
      Swal.fire({
        icon: 'info',
        title: 'Sin datos',
        text: 'No hay información para exportar con el filtro actual.'
      });
      return;
    }

    const esc = (v) =>
      String(v ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

    const headers = ['Marca', 'ID', 'Artículo', 'Color', 'Talla', 'Ubicación', 'Qty',  'Contó', 'Fecha/Hora'];

    const rowsHtml = rowsToExport
      .map((r) => {
        const qty = Number(r.qty || 0);

        const cols = [
          r.brand_name || "",
          r.product_id || "",
          r.article || "",
          r.color_label || "",
          r.size_label || "",
          r.location || "",
          qty,
          r.counted_by_name || "",
          formatCountedAt(r.counted_at),
        ];
        
        return `<tr>${cols.map((c) => `<td>${esc(c)}</td>`).join('')}</tr>`;
      })
      .join('');

    const html = `<!DOCTYPE html>
<html>
<head><meta charset="utf-8" /></head>
<body>
<table border="1">
<thead><tr>${headers.map((h) => `<th>${esc(h)}</th>`).join('')}</tr></thead>
<tbody>${rowsHtml}</tbody>
</table>
</body>
</html>`;

const dateTag = (this.state.reportSessionDate || this.state.reportDate || ymdLocal());
const filename = `reporte_conteos_${reportBrandCurrent}_${dateTag}.xls`;

    const blob = new Blob(['\ufeff', html], { type: 'application/vnd.ms-excel;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 500);
  };

  render() {
    const brands = (this.props.catalog?.brands || []).map((b) => ({ label: b.name, value: b.id }));
    const isAdmin = this.isInventoryAdmin();

    const patternsFiltered = Array.isArray(this.state.patterns) ? this.state.patterns : [];

    const pillStyle = (active = false, fontSize = 12, disabled = false) => {
      const base = {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        padding: '6px 12px',
        borderRadius: 999,
        fontWeight: 800,
        fontSize,
        lineHeight: '14px',
        marginRight: 8,
        marginBottom: 8,
        userSelect: 'none',
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'all 120ms ease'
      };

      if (disabled) {
        return {
          ...base,
          opacity: 0.45,
          background: '#E5E7EB',
          border: '1px solid #CBD5E1',
          color: '#64748B'
        };
      }

      if (active) {
        return {
          ...base,
          background: '#2563EB',
          border: '1px solid #1D4ED8',
          color: '#FFFFFF',
          boxShadow: '0 1px 0 rgba(0,0,0,0.06)'
        };
      }

      return {
        ...base,
        background: '#E5E7EB',
        border: '1px solid #CBD5E1',
        color: '#334155'
      };
    };

    const selectionInfoStyle = { marginTop: 10, marginBottom: 8, fontSize: 16, lineHeight: 1.25 };
    const selectionInfoMuted = { opacity: 0.85, fontWeight: 600 };
    const selectionInfoStrong = { fontWeight: 900 };
    const reportGroups = groupReportRowsByBrand(this.state.reportRows);
    const reportBrandCurrent = (this.state.reportBrandFilter || 'ALL').toString();
    const reportVisibleGroups =
      reportBrandCurrent === 'ALL' ? reportGroups : reportGroups.filter((g) => g.brand === reportBrandCurrent);

    if (isAdmin) {
      const { reportBrands = [], reportBrandSelected } = this.state;

      return (
        <Modal isOpen={this.props.isOpen} isRack={this.props.isRack} onClose={this.handleCloseModal}>
          <Container className="OCContainer">
            <Row>
              <Col s={12}>
                <h4>Panel de Control de Inventario (Admin)</h4>
              </Col>
            </Row>

            <Row>
            <Col s={12} m={6}>
  <h5>Asignar rack a agente</h5>

  <label>Agente</label>
  <AsyncSelect
    cacheOptions
    defaultOptions
    loadOptions={this.loadAgents}
    value={this.state.adminAgent}
    onChange={(opt) => this.setState({ adminAgent: opt || null })}
    placeholder="Buscar agente..."
    isClearable
  />

  <label style={{ marginTop: 10 }}>Rack (base)</label>
  <AsyncSelect
    id="admin-rack"
    placeholder="Selecciona rack (ej. P1-R19)"
    cacheOptions
    defaultOptions
    loadOptions={this.loadAdminAssignRacks}
    value={this.state.adminAssignLocation}
    onChange={(value) => this.setState({ adminAssignLocation: value || null })}
    isClearable
    noOptionsMessage={() => 'Sin resultados'}
  />

  <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12 }}>
    <input
      type="checkbox"
      checked={!!this.state.replaceAllLocations}
      onChange={(e) => this.setState({ replaceAllLocations: e.target.checked })}
    />
    <span style={{ fontWeight: 700 }}>
      Reemplazar TODAS las ubicaciones del agente
    </span>
  </label>

  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 10 }}>
    <Button
      className="green darken-2"
      disabled={this.state.assigningLocation}
      onClick={this.handleAssignLocationToAgent}
    >
      {this.state.assigningLocation ? 'Guardando...' : 'Asignar rack'}
    </Button>

    <Button
      className="red darken-2"
      disabled={this.state.assigningLocation || !this.state.adminAgent?.value}
      onClick={this.handleClearAgentLocations}
      title="Borra todas las ubicaciones del agente en este site"
    >
      LIMPIAR UBICACIONES
    </Button>
  </div>
</Col>

              <Col s={12} m={6}>
  <h5>Reporte de conteos</h5>

  <label>Agente (opcional)</label>
  <AsyncSelect
    cacheOptions
    defaultOptions
    loadOptions={this.loadAgents}
    value={this.state.reportAgent}
    onChange={(opt) => this.setState({ reportAgent: opt || null })}
    placeholder="Buscar agente..."
    isClearable
  />

<label style={{ marginTop: 10 }}>Marca (reporte)</label>
<div style={{ marginBottom: 10 }}>
  {reportBrands.map((b) => {
    const active = reportBrandSelected === b;
    const label = b === 'ALL' ? 'TODAS' : b;

    return (
      <button
        key={b}
        type="button"
        onClick={() => this.setState({ reportBrandSelected: b })}
        style={pillStyle(active, 13)}
        aria-pressed={active}
      >
        {label}
      </button>
    );
  })}
</div>

  <label style={{ marginTop: 10 }}>Día (sesión)</label>
  <input
    type="date"
    value={this.state.reportDate || ''}
    onChange={(e) => this.setState({ reportDate: e.target.value || '' })}
    style={{
      width: '100%',
      height: 40,
      padding: '0 10px',
      borderRadius: 8,
      border: '1px solid #CBD5E1',
      marginBottom: 10
    }}
  />

  {this.state.reportSessionDate ? (
    <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 10 }}>
      Sesión usada: <b>{this.state.reportSessionDate}</b>
      {this.state.reportSessionId ? (
        <>
          {' '}
          · ID:{' '}
          <span style={{ fontFamily: 'monospace' }}>
            {this.state.reportSessionId}
          </span>
        </>
      ) : null}
    </div>
  ) : null}

<Button
  className=""
  disabled={this.state.reportLoading}
  onClick={this.handleLoadReport}
>
  {this.state.reportLoading ? 'Consultando...' : 'Consultar reporte'}
</Button>
</Col>
            </Row>

            <Row style={{ marginTop: 20 }}>
              <Col s={12}>
                {this.state.reportLoading ? (
                  <LoadingView />
                ) : (
                  <div className="inv-report">
                    {this.state.reportRows.length === 0 ? (
                      <div className="inv-report-empty">Sin datos. Da clic en "Consultar reporte" para cargar información.</div>
                    ) : (
                      <>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 12
                          }}
                        >
<div className="brand-filter">
  <button
    type="button"
    onClick={() => this.setState({ reportBrandFilter: 'ALL' })}
    className={reportBrandCurrent === 'ALL' ? 'is-active' : ''}
    aria-pressed={reportBrandCurrent === 'ALL'}
  >
    TODAS ({this.state.reportRows.length})
  </button>

  {reportGroups.map((g) => {
    const active = reportBrandCurrent === g.brand;
    return (
      <button
        key={g.brand}
        type="button"
        onClick={() => this.setState({ reportBrandFilter: g.brand })}
        className={active ? 'is-active' : ''}
        aria-pressed={active}
      >
        {g.brand} ({g.items.length})
      </button>
    );
  })}
</div>

                          <button
                            type="button"
                            className="btn green"
                            onClick={this.handleExportReportExcel}
                            disabled={!this.state.reportRows || this.state.reportRows.length === 0}
                            style={{ whiteSpace: 'nowrap' }}
                            title="Descargar Excel del reporte visible"
                          >
                            DESCARGAR EXCEL
                          </button>
                        </div>

                        <div className="inv-report-scroll" style={{ maxHeight: 520 }}>
                          {reportVisibleGroups.map((g) => (
                            <div key={g.brand} className="inv-report-group">
                              <div className="brand-group-header">
                                <div>
                                  <div className="brand-title">{g.brand}</div>
                                  <div className="brand-subtitle">
                                    {g.items.length} registros · {g.totalQty} piezas
                                  </div>
                                </div>

                                <div className="brand-range">
                                  <div>Inicio: {formatCountedAt(g.firstAt)}</div>
                                  <div>Fin: {formatCountedAt(g.lastAt)}</div>
                                </div>
                              </div>

                              <div className="inv-report-table-wrap">
                              <table className="inv-report-table">
  <colgroup>
    <col style={{ width: "8%" }} />
    <col style={{ width: "10%" }} />
    <col style={{ width: "17%" }} />
    <col style={{ width: "12%" }} />
    <col style={{ width: "5%" }} />
    <col style={{ width: "10%" }} />
    <col style={{ width: "6%" }} />
    <col style={{ width: "12%" }} />
    <col style={{ width: "14%" }} />
    <col style={{ width: "6%" }} /> 
  </colgroup>

  <thead>
    <tr>
      <th>Marca</th>
      <th>ID</th>
      <th>Artículo</th>
      <th>Color</th>
      <th>Talla</th>
      <th>Ubicación</th>
      <th className="cell-num">Piezas</th>
      <th>Contó</th>
      <th className="cell-date">Fecha/Hora</th>
      <th>Acciones</th>
    </tr>
  </thead>

  <tbody>
    {g.items.map((r, i) => {
      const qty = Number(r.qty || 0);
      const dt = formatCountedAt(r.counted_at);
      const dtPretty = (dt || '').replace(', ', ',\n');

      return (
        <tr
          key={`${r.id || r.product_id || 'row'}-${i}`}
          title={`ID DB: ${r.id || '-'}${r.notes ? ` | Nota: ${r.notes}` : ''}`}
        >
          <td className="cell-brand" title={r.brand_name || g.brand || ''}>
            {r.brand_name || g.brand || '-'}
          </td>

          <td className="cell-id" title={r.product_id || ''}>
            {r.product_id || '-'}
          </td>

          <td className="cell-article" title={r.article || ''}>
            {r.article || '-'}
          </td>

          <td className="cell-color" title={r.color_label || ''}>
            {r.color_label || '-'}
          </td>

          <td className="cell-size" title={r.size_label || ''}>
            {r.size_label || '-'}
          </td>

          <td className="cell-location" title={r.location || ''}>
            {r.location || '-'}
          </td>

          <td className="cell-num cell-qty">{qty}</td>

          <td className="cell-user" title={r.counted_by_name || ''}>
            {r.counted_by_name || '-'}
          </td>

          <td className="cell-date">
            <span>{dtPretty}</span>
          </td>

          <td style={{ whiteSpace: 'nowrap' }}>
            <button
              type="button"
              className=""
              style={{ padding: '0 10px', height: 30, lineHeight: '30px' }}
              onClick={() => this.handleEditCountLine(r)}
              disabled={!r.id}
              title={!r.id ? 'Sin id' : 'Editar qty/notas'}
            >
              EDITAR
            </button>
          </td>
        </tr>
      );
    })}
  </tbody>
</table>
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </Col>
            </Row>
          </Container>
        </Modal>
      );
    }

    const headerGenders = Object.values(this.state.header || {});
    const sizesByGender = {};
    headerGenders.forEach((g) => {
      sizesByGender[g.id] = (g.sizes || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));
    });

    return (
      <Modal isOpen={this.props.isOpen} isRack={this.props.isRack} onClose={this.handleCloseModal}>
        <Container className="OCContainer">
          <Row>
            <Col s={12}>
              <h4 className="m3">Ubicación</h4>
              <AsyncSelect
                id="location"
                placeholder="Selecciona una ubicación asignada"
                cacheOptions
                defaultOptions
                loadOptions={this.loadMyLocations}
                value={this.state.location}
                onChange={(value) => this.setState({ location: value })}
                isClearable
                noOptionsMessage={() => 'No tienes racks asignados. Pide al Admin que te asigne uno.'}
              />
            </Col>
          </Row>

          <br />
          <Row>
            <Col s={12}>
              <h4 className="m3">Marca</h4>
              {brands.map((brand) => {
                const active = this.state.brand?.value === brand.value;
                return (
                  <button key={brand.value} onClick={() => this.setBrand(brand)} style={pillStyle(active, 14)} aria-pressed={active}>
                    {brand.label}
                  </button>
                );
              })}

              {this.state.brand && (
                <div style={selectionInfoStyle}>
                  <span style={selectionInfoMuted}>Marca seleccionada:&nbsp;</span>
                  <span style={selectionInfoStrong}>{this.state.brand.label}</span>
                </div>
              )}
            </Col>
          </Row>

          <br />

          {this.state.brand ? (
            this.state.loadingPatterns ? (
              <LoadingView />
            ) : (
              <Row>
                <Col s={12}>
                  {patternsFiltered.length === 0 && (
                    <div style={{ marginTop: 10, fontSize: 13, color: '#666' }}>
                      No hay modelos para <strong>{this.state.brand.label}</strong>.
                    </div>
                  )}

                  {patternsFiltered.map((pattern) => {
                    const isActive = this.state.pressedButtonId === pattern.id;
                    return (
                      <button
                        key={pattern.id}
                        onClick={() => this.setState({ pressedButtonId: pattern.id }, () => this.getProductsFallback(pattern))}
                        style={pillStyle(isActive, 14)}
                        aria-pressed={isActive}
                      >
                        {pattern.name || pattern.Name || pattern.modelName || pattern.description || pattern.id}
                      </button>
                    );
                  })}
                </Col>
              </Row>
            )
          ) : null}

          <h6 className="username">USUARIO:&nbsp;{this.user.name || ''}</h6>

          <Row>
            <Col s={12} style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <Button className="blue darken-2" onClick={this.handleSaveInventory}>
                GUARDAR CONTEO
              </Button>

              <div style={{ fontSize: 13, color: '#666' }}>Captura cantidades por talla y color, luego guarda.</div>
            </Col>
          </Row>

          {this.state.loading ? (
            <LoadingView />
          ) : (
            <Row>
              <Col s={12}>
                <table className="table bordered table-bordered">
                  <thead style={{ position: 'sticky', top: 0, background: '#fff' }}>
                    <tr>
                      <td rowSpan={2}>Color</td>
                      {headerGenders.map((g) => (
                        <td key={g.id} colSpan={(sizesByGender[g.id] || []).length || 1}>
                          {g.name}
                        </td>
                      ))}
                      {headerGenders.length === 0 ? <td>Sin producto</td> : null}
                    </tr>
                    <tr>
                      {headerGenders.map((g) =>
                        (sizesByGender[g.id] || []).map((s) => (
                          <td key={`${g.id}-${s.id}`} style={{ textAlign: 'center' }}>
                            {s.name}
                          </td>
                        ))
                      )}
                      {headerGenders.length === 0 ? <td>-</td> : null}
                    </tr>
                  </thead>

                  <tbody>
                    {(this.state.products.colorItems || []).length === 0 ? (
                      <tr>
                        <td
                          colSpan={Math.max(2, 1 + headerGenders.reduce((acc, g) => acc + (sizesByGender[g.id] || []).length, 0))}
                          style={{ textAlign: 'center', color: '#666' }}
                        >
                          Selecciona tu producto para ver tallas y capturar cantidades.
                        </td>
                      </tr>
                    ) : (
                      (this.state.products.colorItems || []).map((color, cIndex) => (
                        <tr key={color.colorId || cIndex}>
                          <td style={{ minWidth: 180 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              <div
                                style={{
                                  width: 36,
                                  height: 36,
                                  borderRadius: 6,
                                  border: '1px solid #ddd',
                                  ...this.getColorBackground(color)
                                }}
                              />
                              <div style={{ fontSize: 13 }}>
                                <div style={{ fontWeight: 600 }}>{color.colorName}</div>
                                <div style={{ color: '#666' }}>{color.colorId}</div>
                              </div>
                            </div>
                          </td>

                          {headerGenders.map((g) =>
                            (sizesByGender[g.id] || []).map((s) => {
                              const found = this.getProductEditAt(cIndex, g.id, s.id, s.name);
                              if (!found)
                                return (
                                  <td key={`${cIndex}-${g.id}-${s.id}`} style={{ textAlign: 'center', color: '#bbb' }}>
                                    -
                                  </td>
                                );

                              const { product, edit } = found;
                              return (
                                <td key={`${cIndex}-${g.id}-${s.id}`} style={{ textAlign: 'center' }}>
                                  <input
                                    type="number"
                                    min="0"
                                    value={Number(product.qty || 0)}
                                    onChange={(e) => this.modifyProductByPath(edit, e.target.value)}
                                    style={{ width: 70, textAlign: 'center' }}
                                  />
                                </td>
                              );
                            })
                          )}

                          {headerGenders.length === 0 ? <td>-</td> : null}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </Col>
            </Row>
          )}
        </Container>
      </Modal>
    );
  }
}

export default ModalWarehouse;
